import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Play, Pause, Star } from "lucide-react";
import { appearanceOf } from "./appearance";
import { safeUrl } from "./domain";
import { ProductImage, Video } from "./ui";
import { api } from "./data";

export function StoreHeader({ appearance, children }) {
  const [hidden, setHidden] = useState(false);
  const timer = useRef();
  useEffect(() => {
    const scroll = () => {
      clearTimeout(timer.current); setHidden(false);
      if (window.scrollY > 100) timer.current = setTimeout(() => setHidden(true), 1500);
    };
    window.addEventListener("scroll", scroll, { passive: true });
    return () => { clearTimeout(timer.current); window.removeEventListener("scroll", scroll); };
  }, []);
  return <header className={`shop-header floating-header ${hidden ? "is-hidden" : ""}`}
    onFocusCapture={() => { clearTimeout(timer.current); setHidden(false); }}
    onMouseEnter={() => { clearTimeout(timer.current); setHidden(false); }}
    style={{backgroundColor: appearance.headerColor, "--logo-width": `${Math.min(220, Math.max(64, Number(appearance.logoWidth) || 120))}px`}}>{children}</header>;
}

export function StoreHero({ store }) {
  const appearance = appearanceOf(store), slides = appearance.slides;
  const [index, setIndex] = useState(0), [paused, setPaused] = useState(false);
  const current = Math.min(index, slides.length - 1), slide = slides[current];
  const duration = Math.min(30, Math.max(3, Number(slide.duration) || 7));
  useEffect(() => {
    if (!appearance.rotate || paused || slides.length < 2) return;
    const timer = setTimeout(() => setIndex((current + 1) % slides.length), duration * 1000);
    return () => clearTimeout(timer);
  }, [current, slides.length, duration, paused, appearance.rotate]);
  const rotating = appearance.rotate && slides.length > 1;
  return <section className="campaign" aria-label="Novedades de la tienda" aria-roledescription="carrusel">
    <div key={current} className={`campaign-scene ${rotating ? "cycling" : ""}`} style={{"--scene-duration": `${duration}s`, animationPlayState: paused ? "paused" : "running"}}>
      <div className="campaign-background" style={{backgroundColor: slide.color || "#e9dff3", backgroundImage: safeUrl(slide.background) ? `url("${safeUrl(slide.background)}")` : undefined}} />
      <div className="campaign-copy">
        <span className="hero-kicker">{slide.kicker}</span>
        <h1>{slide.title}</h1><p>{slide.subtitle}</p>
        <button className="hero-cta" onClick={() => document.getElementById("catalogo")?.scrollIntoView({behavior: "smooth"})}>Explorar la colección <ArrowUpRight size={20}/></button>
      </div>
      {safeUrl(slide.foreground) && <div className="campaign-figure"><img src={safeUrl(slide.foreground)} alt={slide.imageAlt || "Selección de Dilo Shop"} style={{animationPlayState: paused ? "paused" : "running"}} /></div>}
    </div>
    <div className="campaign-controls" aria-label="Elegir banner">
      {slides.map((s, i) => <button key={s.id || i} className={`campaign-dot ${i === current ? "active" : ""}`} aria-label={`Mostrar banner ${i + 1}`} aria-pressed={i === current} onClick={() => setIndex(i)} />)}
      {rotating && <button className="campaign-pause" aria-label={paused ? "Reanudar banners" : "Pausar banners"} onClick={() => setPaused(!paused)}>{paused ? <Play size={13}/> : <Pause size={13}/>}</button>}
    </div>
  </section>;
}

export function MediaGallery({ product, variant }) {
  const photos = [...new Set([variant?.image, ...(product.images || [])].map(safeUrl).filter(Boolean))];
  const media = [...photos.map(src => ({type: "image", src})), ...(safeUrl(product.video_url) ? [{type:"video", src:product.video_url}] : [])];
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [variant?.id, product.id]);
  const active = Math.min(index, Math.max(0, media.length - 1)), item = media[active];
  const go = delta => setIndex((active + delta + media.length) % media.length);
  return <div className="media-gallery">
    <div className="media-stage" aria-label="Fotos y video del producto">
      {item?.type === "video" ? <Video key={item.src} url={item.src}/> : item ? <img src={item.src} alt={`${product.name} · ${variant?.color || ""} · foto ${active + 1}`}/> : <ProductImage product={product} variant={variant}/>}
      {media.length > 1 && <><button className="media-prev" aria-label="Imagen o video anterior" onClick={() => go(-1)}><ChevronLeft/></button><button className="media-next" aria-label="Siguiente imagen o video" onClick={() => go(1)}><ChevronRight/></button><span className="media-counter">{active + 1} / {media.length}</span></>}
    </div>
    {media.length > 1 && <div className="media-thumbs">{media.map((m,i) => <button key={m.src} aria-label={m.type === "video" ? "Ver video" : `Ver foto ${i + 1}`} aria-pressed={active === i} onClick={() => setIndex(i)}>{m.type === "video" ? <><Play size={18}/><small>Video</small></> : <img src={m.src} alt=""/>}</button>)}</div>}
  </div>;
}
export function ProductReviews({ productId }) {
  const [reviews, setReviews] = useState(null), [error, setError] = useState(false);
  useEffect(() => {
    let stop = false; setError(false); setReviews(null);
    api.productReviews(productId).then(r => { if (!stop) setReviews(r); }).catch(() => { if (!stop) setError(true); });
    return () => { stop = true; };
  }, [productId]);
  const stars = rating => <span className="review-stars" aria-label={`${rating} de 5 estrellas`}>{[1,2,3,4,5].map(n => <Star key={n} size={16} fill={n <= Math.round(rating) ? "currentColor" : "none"}/>)}</span>;
  return <section className="product-reviews"><h3>Opiniones de compradores</h3>
    {error ? <p className="muted">No pudimos cargar las opiniones. Intenta abrir el producto nuevamente.</p> : !reviews ? <p>Cargando opiniones…</p> : <>
      <div className="flex">{stars(reviews.average)}<strong>{reviews.count ? `${reviews.average} / 5` : "Sin calificaciones aún"}</strong><span className="muted">({reviews.count})</span></div>
      <p className="file-note">Opiniones de compras entregadas que incluyen este producto. Puedes dejar la tuya desde el enlace de tu pedido.</p>
      {reviews.items.map((r,i) => <article key={i}>{stars(r.rating)}<small>Compra verificada · {new Date(r.date).toLocaleDateString("es-PE")}</small><p className="pre-line">{r.text}</p></article>)}
    </>}
  </section>;
}
